import { SecureStorage } from '@aparajita/capacitor-secure-storage'

// Prefix scoped to this app's auth data so a later feature using the plugin's
// default store can't collide with these keys.
const KEY_PREFIX = 'auth.'
let prefixSet = false

const ensurePrefix = async (): Promise<void> => {
  if (prefixSet) return
  await SecureStorage.setKeyPrefix(KEY_PREFIX)
  prefixSet = true
}

interface IEnvelope<T> {
  payload: T
  expiration?: number
  createdAt: number
}

class SecureSessionStorage {
  async get<T = unknown>(key: string, now: number = Date.now()): Promise<T | undefined> {
    try {
      await ensurePrefix()
      const data = await SecureStorage.get(key, false, false)
      // Plugin contract: null means no item with this key, not an error — the
      // ordinary "not logged in" case, not worth logging.
      if (data == null) return undefined

      const { payload, createdAt, expiration } = data as unknown as IEnvelope<T>
      const MILLISECONDS_IN_SECOND = 1000
      if (expiration && now - createdAt > expiration * MILLISECONDS_IN_SECOND) {
        await SecureStorage.remove(key, false)
        return undefined
      }
      return payload
    } catch (err) {
      // A genuine Keystore/plugin failure, distinct from "no value" above — logged
      // so it's distinguishable from a legitimate logged-out state in device logs.
      console.warn('[SecureSessionStorage] read failed', err)
      return undefined
    }
  }

  async set(
    key: string,
    data: unknown,
    expiration?: number,
    createdAt: number = Date.now()
  ): Promise<void> {
    try {
      await ensurePrefix()
      const envelope: IEnvelope<unknown> = { payload: data, expiration, createdAt }
      await SecureStorage.set(key, envelope as unknown as Record<string, unknown>, false, false)
    } catch (err) {
      // Matches BrowserStorage's existing swallow-on-write behavior — a transient
      // encrypted-write failure shouldn't fail the login/action that triggered it.
      console.warn('[SecureSessionStorage] write failed', err)
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await ensurePrefix()
      await SecureStorage.remove(key, false)
    } catch (err) {
      console.warn('[SecureSessionStorage] remove failed', err)
    }
  }
}

export default new SecureSessionStorage()
